import { api, fetchWithAuth } from './api';

export interface PoolDocument {
  id: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  extraction_status: 'pending' | 'running' | 'done' | 'completed' | 'error' | 'failed';
  extraction_error?: string | null;
  tagging_status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  tagging_error?: string | null;
  created_at: string;
  insight_count: number;
  strategy_id?: number | null;
}

export interface ClusterDetail {
  id: number;
  document_id: number;
  cluster: string;
  insight_type: string;
  title: string;
  insight_text: string;
  scope_type: 'global' | 'specific' | 'unallocated';
  assigned_strategy_ids: number[];
  ai_suggested_scope?: {
    scope?: string;
    strategy_ids?: number[];
    rationale?: string;
  } | null;
}

export interface ExpansionSuggestion {
  source_niche: string;
  new_sub_niche: string;
  avatar_hint: string;
  angle: string;
  rationale: string;
  suggested_insight_ids: number[];
  resource_matches: { insight_id: number; why: string }[];
  missing_resources_note?: string | null;
  briefing: string;
  confidence: number;
}

export interface ExpansionRun {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  suggestions?: ExpansionSuggestion[];
  focus_niches: string[];
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface ExpansionContext {
  strategies: {
    id: number;
    name: string;
    niche: string | null;
    sub_niche?: string | null;
    avatar_hint?: string;
    account_language?: string | null;
  }[];
  pool_size: number;
}

export interface ChooseResponse {
  choice_id: number;
  initial_niche: string;
  initial_sub_niche: string;
  initial_briefing: string;
  initial_language?: string | null;
  assign_insight_ids: number[];
  source_expansion_run_id: number;
}

export const expansionAdvisorApi = {
  listPool: () => api.get<PoolDocument[]>('/expansion/pool'),

  uploadToPool: async (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const res = await fetchWithAuth('/api/expansion/pool', { method: 'POST', body: form });
    return res.json();
  },

  deleteFromPool: (docId: number) =>
    api.delete<{ ok: boolean }>(`/expansion/pool/${docId}`),

  retryTagging: (docId: number) =>
    api.post<{ ok: boolean; tagging_status: string }>(`/expansion/pool/${docId}/retry-tagging`),

  listDocInsights: (docId: number) =>
    api.get<ClusterDetail[]>(`/expansion/pool/${docId}/insights`),

  updateInsightScope: (
    insightId: number,
    body: { scope_type: 'global' | 'specific' | 'unallocated'; strategy_ids: number[] }
  ) => api.patch<{ ok: boolean }>(`/expansion/insights/${insightId}/scope`, body),

  bulkUpdateScope: (body: {
    insight_ids: number[];
    scope_type: 'global' | 'specific' | 'unallocated';
    strategy_ids: number[];
  }) => api.post<{ updated: number }>('/expansion/insights/bulk-scope', body),

  getContext: () => api.get<ExpansionContext>('/expansion/context'),

  generateSuggestions: (focusNiches: string[]) =>
    api.post<{
      run_id: number;
      status: string;
      suggestions: ExpansionSuggestion[];
      warnings: string[];
    }>('/expansion/run', { focus_niches: focusNiches }),

  getRun: (runId: number) => api.get<ExpansionRun>(`/expansion/runs/${runId}`),

  listRuns: (limit = 20) => api.get<ExpansionRun[]>(`/expansion/runs?limit=${limit}`),

  chooseSuggestion: (runId: number, suggestionIndex: number) =>
    api.post<ChooseResponse>(`/expansion/runs/${runId}/choose`, {
      suggestion_index: suggestionIndex,
    }),
};
